<template>
  <div class="menu-contextual-tabla-acuerdos-comerciales">
    <v-menu offset-y content-class="menu-contextual-custom">
      <template v-slot:activator="{ on, attrs }">
        <v-btn icon v-bind="attrs" v-on="on" class="boton-menu-contextual">
          <v-icon class="icono-menu-contextual">more_horiz</v-icon>
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-for="(accion, i) in acciones"
          :key="i"
          :disabled="accion.deshabilitado"
          class="menu-item"
          :class="{ 'menu-item-separador': accion.separador }"
          @click="accion.evento_a_emitir"
        >
          <v-list-item-content>
            <div class="menu-item-content">

              <v-icon
                class="icono-accion material-symbols-outlined"
                :class="{
                  'icono-accion--deshabilitado': accion.deshabilitado,
                  'icono-accion--activo': !accion.deshabilitado,
                  'texto-danger': !accion.deshabilitado && accion.critica,
                  'icono-accion--editar': accion.icono === 'edit',
                  'icono-accion--editar-deshabilitado': accion.icono === 'edit' && accion.deshabilitado
                }"
              >
                {{ accion.icono }}
              </v-icon>

              <div
                class="label-accion"
                :class="{
                  'label-accion--deshabilitado': accion.deshabilitado,
                  'label-accion--activo': !accion.deshabilitado,
                  'texto-danger': !accion.deshabilitado && accion.critica
                }"
              >
                {{ accion.label }}
              </div>
            </div>

          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { AcuerdoComercial } from '@/models/AcuerdoComercial';
import { AccionesSobreAcuerdosComerciales, TipoAcuerdo } from '../acuerdo-comercial.consts';
import { EventBus } from '@/main';
import { isEqual } from 'lodash';

/**
 * Menú contextual para la tabla de acuerdos comerciales
 * 
 * Será invocado por cada fila, vinculado al acuerdo comercial mostrado
 * Emite eventos por cada acción disponible, estos eventos sirven para identificar la acción a realizar
 * En caso de estar deshabilitada la acción, no emitirá.
 */
export default {
  name: 'MenuContextualTablaAcuerdosComerciales',
  props: {
    acuerdo_comercial: { type: AcuerdoComercial, default: null } // Recibira, en un atributo, las acciones a disponibles
  },
  computed: {

    /**
     * Acciones a realizar dependiendo del tipo de acuerdo recibido
     */
    acciones() {

      /**
       * Lista de atributos
       * - label: Texto a mostrar en el item de la lista
       * - icono: Icono a mostrar al lado izquierdo del texto
       * - evento_a_emitir: Evento que se emitira de manera GLOBAL, a través del EventBus para poder ser capturado en cualquier componente y así evitar propagación de eventos
       *                    Este evento va a notificar que acción se debe realizar sobre el acuerdo comercial
       * - deshabilitado: Indica si el item está deshabilitado, este depende de la lista de acciones, y agrega el estilo correspondiente
       * - critica: Indica si el item es una acción crítica, y agrega el estilo correspondiente (pinta de rojo el icono y label)
       * - separador: Indica si el item es un separador, y agrega el estilo correspondiente (modifica el alto, padding y borde)
       */

      // Se formula la lista de acciones posibles con las que siempre se pueden (lo define el front) y con las que son dinamicas dependiendo el estado que la define el backend.

      // En todos los AC siempre se puede ver el detalle y el historial (definición de front)
      const acciones_posibles = [
        AccionesSobreAcuerdosComerciales.DETALLES,
        AccionesSobreAcuerdosComerciales.HISTORIAL
      ];

      // Para el resto de acciones que dependen del estado (definición de backend)
      if (this.acuerdo_comercial.acciones_posibles) {
        acciones_posibles.push(...this.acuerdo_comercial.acciones_posibles);
      }

      // Acciones en común para todos los tipos de acuerdo
      let lista_acciones = [
        {
          label: 'Ver detalles',
          icono: 'notes',
          evento_a_emitir: () => { EventBus.$emit('sidesheet-detalles:abrir', this.acuerdo_comercial); },
          deshabilitado: !acciones_posibles.includes(AccionesSobreAcuerdosComerciales.DETALLES)
        },
        {
          label: 'Ver historial',
          icono: 'history', evento_a_emitir: () => { EventBus.$emit('sidesheet-detalles:abrir-historial', this.acuerdo_comercial); }, deshabilitado: !acciones_posibles.includes(AccionesSobreAcuerdosComerciales.HISTORIAL)
        },
        { label: '', icono: '', evento_a_emitir: () => { null; }, deshabilitado: false, separador: true }, // Separador
        {
          label: 'Editar acuerdo',
          icono: 'edit',
          evento_a_emitir: () => { EventBus.$emit('sidesheet-edicion:abrir', this.acuerdo_comercial); },
          deshabilitado: !acciones_posibles.includes(AccionesSobreAcuerdosComerciales.EDITAR),
        },
      ];

      switch (this.acuerdo_comercial.tipo_propuesta) {
      case TipoAcuerdo.CONSORCIO:
        lista_acciones = [
          ...lista_acciones,
          ...[
            {
              label: 'Extender período de aceptación',
              icono: 'timer',
              evento_a_emitir: () => { EventBus.$emit('modal-extender:abrir', this.acuerdo_comercial); },
              deshabilitado: !acciones_posibles.includes(AccionesSobreAcuerdosComerciales.EXTENDER_TIEMPO)
            },
            // Solo mostramos la acción de finalizar periodo de aceptación si el usuario logueado es el que creó el acuerdo comercial
            ...(isEqual(this.acuerdo_comercial.usuario_sales, this.$store.getters.user.email)
              ? [{
                label: 'Finalizar período de aceptación',
                icono: 'timer_off',
                evento_a_emitir: () => { EventBus.$emit('modal-finalizar-periodo:abrir', this.acuerdo_comercial); },
                deshabilitado: !acciones_posibles.includes(AccionesSobreAcuerdosComerciales.FINALIZAR_PERIODO_ACEPTACION)
              }]
              : []),
            { label: '', icono: '', evento_a_emitir: () => {}, deshabilitado: false, separador: true }, // Separador
          ]
        ];
        break;
      case TipoAcuerdo.ADMINISTRACION:
        lista_acciones.push({ label: '', icono: '', evento_a_emitir: () => {}, deshabilitado: false, separador: true }); // Separador
        break;
      }

      // Acciones en común para todos los tipos de acuerdo, se agrega al final
      lista_acciones.push({
        label: 'Eliminar acuerdo',
        icono: 'delete',
        evento_a_emitir: () => { EventBus.$emit('sidesheet-eliminacion:abrir', this.acuerdo_comercial); },
        deshabilitado: !acciones_posibles.includes(AccionesSobreAcuerdosComerciales.ELIMINAR),
        critica: true
      });

      return lista_acciones;
    },
  }
};
</script>

<style lang="scss">

  .menu-contextual-tabla-acuerdos-comerciales {
    .boton-menu-contextual {
      width: 24px;
      height: 24px;

      .icono-menu-contextual {
        color: color(celeste-claro);
      }
    }
  }

  .menu-contextual-custom {

    .menu-item {
      min-height: 40px; // Piso estilo aplicado por list-item
      height: 40px;
      padding: 8px 20px;

      .menu-item-content {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;

        .icono-accion {
          font-size: 18px;

          &--activo {
            color: color(dark);
          }

          &--deshabilitado {
            color: color(gris-deshabilitado);
          }

          &--editar {
            color: color(dark);
            font-variation-settings:
            'FILL' 1,
            'wght' 400,
            'GRAD' 0,
            'opsz' 24
          }

          &--editar-deshabilitado {
            color: color(gris-deshabilitado);
          }
        }

        .label-accion {
          font-size: 14px;
          font-weight: 400;
          line-height: 19.6px;

          &--activo {
            color: color(dark);
          }

          &--deshabilitado {
            color: color(gris-deshabilitado);
          }
        }

        .texto-danger {
          color: color(rojo-error);
        }
      }
    }

    .menu-item-separador {
      min-height: 1px; // Piso estilo aplicado por list-item
      height: 1px;
      padding: 0;
      border-bottom: 1px solid color(celeste-claro);
      cursor: default;
    }

    // Ajuste para que el separador no agregue padding
    .v-list-item__content {
      padding: 0;
    }
  }

</style>